import { useEffect, useState } from "react";
import { selectCustomersLoadingPag, selectCustomersPag } from "../../redux/customers/selectors";
import { AdminTable } from "./AdminTable";
import { useDispatch, useSelector } from "react-redux";
import { Column } from "devextreme-react/data-grid";
import { createCustomer, deleteCustomer, editCustomer, getCustomersPag, toggleActiveCustomer } from "../../redux/customers/thunks";
import { openModal } from "../../redux/modal/modalSlice";
import { useTranslation } from "react-i18next";
import CustomSwitch from "../common/CustomSwitch";
import CustomerServicesDetail from "./CustomerServicesDetail";

const initialSortOptions = {
    columna: "customerId",
    sortOrderAsc: true
};

function CustomerManagement() {
    const { t } = useTranslation();
    const customers = useSelector(selectCustomersPag);
    const dispatch = useDispatch();
    const loading = useSelector(selectCustomersLoadingPag);
    const [sortOptions, setSortOptions] = useState(initialSortOptions);

    useEffect(() => {
        getListCustomers(mapPageInfo(customers));
    }, []);

    function mapPageInfo(customersData) {
        return {
            pageIndex: customersData.pageIndex ?? 0,
            pageSize: customersData.pageSize ?? 10,
            orderByField: customersData.columna ?? "customerId",
            orderByAscending: customersData.sortOrderAsc ?? true
        };
    }

    const getListCustomers = (pageInfo) => {
        let query = `?pageIndex=${pageInfo.pageIndex}&pageNum=${pageInfo.pageSize}`;
        if (pageInfo.searchString?.trim().length > 0) {
            query = query.concat(`&searchString=${pageInfo.searchString.trim()}`);
        }
        if (pageInfo.orderByField) {
            query = query.concat(`&orderByField=${pageInfo.orderByField}&orderByAscending=${pageInfo.orderByAscending}`);
        }
        dispatch(getCustomersPag({ query }));
    };

    const handleToggleActive = (e, rowData) => {
        e.stopPropagation();
        dispatch(toggleActiveCustomer({ param: rowData.customerId })).then((data) => {
            if (data.error) {
                dispatch(openModal({ type: "warning", message: data.error.message }));
            } else {
                getListCustomers(mapPageInfo({ ...customers, ...sortOptions }));
            }
        });
    };

    const columnsCustomers = [
        <Column sortIndex={1} key={"customer-2"} dataField="customerCode" caption={t("CustomerCode")} dataType="string" />,
        <Column key={"customer-3"} dataField="customerName" caption={t("CustomerName")} dataType="string" />,
        <Column key={"customer-4"} dataField="costCenter" caption={t("CostCenter")} dataType="string" />,
        <Column
            key={"customer-5"}
            dataField="active"
            caption={t("Status")}
            width={100}
            allowEditing={false}
            allowCreating={false}
            alignment="center"
            cellRender={(cellData) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    <CustomSwitch
                        checked={Boolean(cellData.data.active)}
                        onChange={(e) => handleToggleActive(e, cellData.data)}
                    />
                </div>
            )}
        />
    ];

    function handleSaveNewCustomerClick(value, closeCreateModal) {
        const customerName = (value.customerName || value.name)?.trim();
        if (customerName) {
            const nameExists = customers.elements.some(customer => (customer.customerName || customer.name)?.toLowerCase() === customerName.toLowerCase());
            if (nameExists) {
                dispatch(openModal({ type: "warning", message: t("CustomerAlreadyExists") }));
            } else {
                const customerParams = {
                    body: {
                        customerName: customerName,
                        name: customerName,
                        customerCode: value.customerCode?.trim() || null,
                        costCenter: value.costCenter?.trim() || null,
                        active: true
                    }
                };
                dispatch(createCustomer(customerParams)).then((data) => {
                    if (data.error) {
                        dispatch(openModal({ type: "warning", message: data.error.message }));
                    } else {
                        getListCustomers(mapPageInfo({ ...customers, ...sortOptions }));
                        closeCreateModal();
                    }
                });
            }
        } else {
            closeCreateModal();
        }
    }

    const handleEditClick = (value, closeEditModal) => {
        const customerName = (value.customerName || value.name)?.trim();
        if (customerName) {
            const nameExists = customers.elements.some(customer => (customer.customerName || customer.name)?.toLowerCase() === customerName.toLowerCase());
            const currentCustomer = customers.elements.find(customer => customer.customerId === value.customerId);
            const currentCustomerName = currentCustomer?.customerName || currentCustomer?.name;
            if (nameExists && customerName.toLowerCase() !== currentCustomerName?.toLowerCase()) {
                dispatch(openModal({ type: "warning", message: t("CustomerAlreadyExists") }));
            } else {
                const editCustomerParams = {
                    param: value.customerId,
                    customerId: value.customerId,
                    body: {
                        customerName: customerName,
                        name: customerName,
                        customerCode: value.customerCode?.trim() || null,
                        costCenter: value.costCenter?.trim() || null
                    }
                };
                dispatch(editCustomer(editCustomerParams)).then((data) => {
                    if (data.error) {
                        dispatch(openModal({ type: "warning", message: data.error.message }));
                    } else {
                        getListCustomers(mapPageInfo({ ...customers, ...sortOptions }));
                        closeEditModal();
                    }
                });
            }
        } else {
            closeEditModal();
        }
    };

    const handleDeleteClick = (customerId, closeDeleteModal) => {
        dispatch(deleteCustomer({ param: customerId })).then((data) => {
            if (data.error) {
                dispatch(openModal({ type: "warning", message: data.error.message }));
            } else {
                let updatedPageIndex = customers.pageIndex;
                if (customers.elements.length === 1) {
                    updatedPageIndex = Math.max(0, customers.pageIndex - 1);
                }
                const updatedCustomers = {
                    ...customers,
                    pageIndex: updatedPageIndex,
                };
                getListCustomers(mapPageInfo({ ...updatedCustomers, ...sortOptions }));
                closeDeleteModal();
            }
        });
    };

    function allowDelete(rowData) {
        return rowData.data.custom;
    }

    return (
        <div style={{ width: "100%" }}>
            <AdminTable
                sectionTitle={t("Customers")}
                masterTitle={t("Customer")}
                itemIdField={"customerId"}
                itemDescriptionField={"customerName"}
                loading={loading}
                getData={getListCustomers}
                dataSource={customers}
                columnasTabla={columnsCustomers}
                onCreate={handleSaveNewCustomerClick}
                onDelete={handleDeleteClick}
                onEdit={handleEditClick}
                numElements={[10, 20, 30]}
                allowDelete={allowDelete}
                sortField={sortOptions}
                setsortField={setSortOptions}
                master={true}
                masterDetail={CustomerServicesDetail}
            />
        </div>
    );
}

export default CustomerManagement;
